import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ComposedChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  BankReconciliation,
  CleanerPayAudit,
  ConversionFunnel,
  GrowthInsight,
  Headline,
  InsightAction,
  LeadSourceRow,
  LongTermSummary,
  PostcodeRow,
  RepeatCustomers,
  RevenueTrendPoint,
  SpendTrend,
  Stage,
  TopPayeeRow,
} from "@breeze/shared";
import { TopNav } from "./TopNav.js";
import {
  fetchBankReconciliation,
  fetchCleanerPayAudit,
  fetchConversionFunnel,
  fetchHeadline,
  fetchInsights,
  fetchLatestActions,
  fetchLatestSpendCutActions,
  fetchLeadSources,
  fetchLongTerm,
  fetchRepeatCustomers,
  fetchRevenueTrend,
  fetchSpendCuts,
  fetchSpendTrend,
  fetchTopPayees,
  fetchTopPostcodes,
  patchAction,
  refreshInsight,
  refreshSpendCuts,
  syncAds,
} from "../api.js";
import { BusinessGrowthExpenses } from "./BusinessGrowthExpenses.js";
import { StrategyProgressSection } from "./StrategyProgress.js";

// ===========================================================================
// Business Growth dashboard.
//
// Composition: this file owns the page shell + Phase 1 income widgets +
// Phase 3 P&L summary. Phase 2 expenses live in BusinessGrowthExpenses.
//
// Design rules per memory:
//   - text-ink-50 cream for all readable text (no aubergine for text)
//   - large minimum font sizes
//   - dark but not too dark (bg-ink-800)
//   - one font (Open Sans) — no font-display swap
//   - charts use stage palette colours (cyan/yellow/orange/purple/green/pink)
// ===========================================================================

type Props = { userName: string };

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function thisMonthIso(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

function shiftMonth(month: string, delta: number): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
}

function monthLabel(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const names = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${names[Number(m[2]) - 1]} ${m[1]}`;
}

function formatGBP(amount: number, opts: { decimals?: 0 | 2 } = {}): string {
  const decimals = opts.decimals ?? 2;
  const abs = Math.abs(amount).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return amount < 0 ? `−£${abs}` : `£${abs}`;
}

function formatPct(fraction: number, decimals = 1): string {
  return `${(fraction * 100).toFixed(decimals)}%`;
}

function formatDelta(pct: number | null): { text: string; tone: "good" | "bad" | "neutral" } {
  if (pct === null) return { text: "—", tone: "neutral" };
  const sign = pct > 0 ? "+" : "";
  const tone = pct > 0 ? "good" : pct < 0 ? "bad" : "neutral";
  return { text: `${sign}${pct.toFixed(1)}%`, tone };
}

const STAGE_COLOR: Record<Stage, string> = {
  lead: "#36C5F0",
  opportunity: "#ECB22E",
  chase: "#F07B3F",
  schedule: "#7F6DC5",
  won: "#2EB67D",
  lost: "#E01E5A",
  // Tech cards don't appear in the Business Growth funnel charts (they're
  // a parallel track), but the Record<Stage,...> shape requires a value.
  tech: "#A678A8",
};

const STAGE_LABEL: Record<Stage, string> = {
  lead: "Lead",
  opportunity: "Opportunity",
  chase: "Chase",
  schedule: "Schedule",
  won: "Won",
  lost: "Lost",
  tech: "Technicians",
};

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function BusinessGrowth({ userName }: Props): JSX.Element {
  const [month, setMonth] = useState<string>(thisMonthIso());

  const periodDays = 90;
  const trendMonths = 13;

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav userName={userName} />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
                Business Growth
              </div>
              <h1 className="mt-1 text-2xl font-semibold text-ink-50">
                {monthLabel(month)}
              </h1>
            </div>
            <MonthPicker month={month} onChange={setMonth} />
          </div>

          <StrategyProgressSection />
          <ThisWeekFocusSection />
          <SpendCutsSection />
          <GrowthInsightSection />
          <HeadlineSection month={month} />
          <RevenueTrendSection months={trendMonths} />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ConversionFunnelSection month={month} />
            <RepeatCustomersSection days={periodDays} />
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <TopPostcodesSection days={periodDays} />
            <LeadSourcesSection days={periodDays} />
          </div>

          <LongTermSection />

          <TopPayeesSection />
          <SpendTrendSection />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <BankReconciliationSection />
            <CleanerPayAuditSection />
          </div>

          <BusinessGrowthExpenses month={month} />
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month picker
// ---------------------------------------------------------------------------

function MonthPicker({
  month,
  onChange,
}: {
  month: string;
  onChange: (m: string) => void;
}): JSX.Element {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  const year = m ? Number(m[1]) : new Date().getFullYear();
  const mon = m ? Number(m[2]) : new Date().getMonth() + 1;
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const years = useMemo(() => {
    const now = new Date().getUTCFullYear();
    const arr: number[] = [];
    for (let y = now; y >= 2024; y--) arr.push(y);
    return arr;
  }, []);
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onChange(shiftMonth(month, -1))}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50 hover:bg-ink-600"
        title="Previous month"
      >
        ←
      </button>
      <select
        value={mon}
        onChange={(e) => onChange(`${year}-${pad(Number(e.target.value))}`)}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
      >
        {months.map((label, i) => (
          <option key={i} value={i + 1}>{label}</option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => onChange(`${e.target.value}-${pad(mon)}`)}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
      >
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
      <button
        onClick={() => onChange(shiftMonth(month, 1))}
        disabled={month >= thisMonthIso()}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50 hover:bg-ink-600 disabled:opacity-40"
        title="Next month"
      >
        →
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Headline strip — 4 metric tiles
// ---------------------------------------------------------------------------

function HeadlineSection({ month }: { month: string }): JSX.Element {
  const q = useQuery({
    queryKey: ["growth", "headline", month],
    queryFn: () => fetchHeadline(month),
  });
  return (
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <HeadlineTile
        label="Revenue"
        value={q.data ? formatGBP(q.data.revenue.current, { decimals: 0 }) : "—"}
        loading={q.isLoading}
        deltaMonth={q.data?.revenue.prevMonthDeltaPct ?? null}
        deltaYear={q.data?.revenue.prevYearDeltaPct ?? null}
      />
      <HeadlineTile
        label="Job count"
        value={q.data ? String(q.data.jobCount.current) : "—"}
        loading={q.isLoading}
        deltaMonth={q.data?.jobCount.prevMonthDeltaPct ?? null}
        deltaYear={q.data?.jobCount.prevYearDeltaPct ?? null}
      />
      <HeadlineTile
        label="Avg job value"
        value={q.data ? formatGBP(q.data.averageJobValue.current, { decimals: 0 }) : "—"}
        loading={q.isLoading}
        deltaMonth={q.data?.averageJobValue.prevMonthDeltaPct ?? null}
        deltaYear={q.data?.averageJobValue.prevYearDeltaPct ?? null}
      />
      <HeadlineTile
        label="Lead → Won"
        value={q.data ? formatPct(q.data.conversionRate.current) : "—"}
        loading={q.isLoading}
        deltaMonth={q.data?.conversionRate.prevMonthDeltaPct ?? null}
        deltaYear={q.data?.conversionRate.prevYearDeltaPct ?? null}
      />
    </section>
  );
}

function HeadlineTile({
  label,
  value,
  loading,
  deltaMonth,
  deltaYear,
}: {
  label: string;
  value: string;
  loading: boolean;
  deltaMonth: number | null;
  deltaYear: number | null;
}): JSX.Element {
  const dm = formatDelta(deltaMonth);
  const dy = formatDelta(deltaYear);
  const toneCls = (t: "good" | "bad" | "neutral"): string =>
    t === "good"
      ? "text-stage-won"
      : t === "bad"
      ? "text-stage-lost"
      : "text-ink-100";
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-700 p-5 shadow-card">
      <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold tabular-nums text-ink-50">
        {loading ? "…" : value}
      </div>
      <div className="mt-3 flex items-baseline gap-3 text-[13px]">
        <span className={`${toneCls(dm.tone)} tabular-nums`}>{dm.text}</span>
        <span className="text-ink-100">vs last month</span>
      </div>
      <div className="mt-1 flex items-baseline gap-3 text-[13px]">
        <span className={`${toneCls(dy.tone)} tabular-nums`}>{dy.text}</span>
        <span className="text-ink-100">vs same month last year</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revenue trend — bar chart with line overlay
// ---------------------------------------------------------------------------

function RevenueTrendSection({ months }: { months: number }): JSX.Element {
  const q = useQuery({
    queryKey: ["growth", "trend", months],
    queryFn: () => fetchRevenueTrend(months),
  });
  const data = (q.data ?? []).map((p) => ({
    month: monthLabel(p.month),
    revenue: p.revenue,
    jobCount: p.jobCount,
  }));
  return (
    <Section title="Revenue trend" subtitle={`Last ${months} months`}>
      {q.isLoading ? (
        <Loading />
      ) : data.length === 0 ? (
        <Empty />
      ) : (
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#332D3A" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 13 }} />
              <YAxis
                yAxisId="left"
                stroke="#F0F0F0"
                tick={{ fill: "#F0F0F0", fontSize: 13 }}
                tickFormatter={(v: number) => formatGBP(v, { decimals: 0 })}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#D8D8D8"
                tick={{ fill: "#D8D8D8", fontSize: 13 }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: "#221F27",
                  border: "1px solid #4A4358",
                  color: "#FFFFFF",
                }}
                formatter={(value, name) => {
                  const v = Number(value ?? 0);
                  if (name === "revenue") return [formatGBP(v, { decimals: 0 }), "Revenue"];
                  if (name === "jobCount") return [String(v), "Jobs"];
                  return [String(value ?? ""), String(name ?? "")];
                }}
              />
              <Bar yAxisId="left" dataKey="revenue" fill="#2EB67D" radius={[4, 4, 0, 0]} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="jobCount"
                stroke="#36C5F0"
                strokeWidth={2}
                dot={{ fill: "#36C5F0", r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Conversion funnel — cards created in month, distribution by current stage
// ---------------------------------------------------------------------------

function ConversionFunnelSection({ month }: { month: string }): JSX.Element {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  const year = m ? Number(m[1]) : new Date().getFullYear();
  const mon = m ? Number(m[2]) : new Date().getMonth() + 1;
  const start = `${year}-${pad(mon)}-01`;
  const lastDay = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  const end = `${year}-${pad(mon)}-${pad(lastDay)}`;

  const q = useQuery({
    queryKey: ["growth", "funnel", start, end],
    queryFn: () => fetchConversionFunnel(start, end),
  });
  return (
    <Section
      title="Conversion funnel"
      subtitle={`Of leads created in ${monthLabel(month)} — where are they now`}
    >
      {q.isLoading ? <Loading /> : !q.data ? <Empty /> : <FunnelChart funnel={q.data} />}
    </Section>
  );
}

function FunnelChart({ funnel }: { funnel: ConversionFunnel }): JSX.Element {
  const total = funnel.stages.reduce((s, x) => s + x.count, 0);
  if (total === 0) return <Empty message="No leads in this window." />;
  return (
    <div className="space-y-3">
      {funnel.stages.map((s) => {
        const pct = total > 0 ? (s.count / total) * 100 : 0;
        return (
          <div key={s.stage} className="flex items-center gap-3">
            <div className="w-28 shrink-0 text-[15px] font-semibold text-ink-50">
              {STAGE_LABEL[s.stage]}
            </div>
            <div className="relative h-8 flex-1 overflow-hidden rounded-md bg-ink-800">
              <div
                className="h-full"
                style={{
                  width: `${pct}%`,
                  background: STAGE_COLOR[s.stage],
                  opacity: 0.85,
                }}
              />
              <div className="absolute inset-0 flex items-center px-3 text-[15px] font-semibold text-ink-50 drop-shadow">
                {s.count}
                <span className="ml-2 text-[13px] font-normal text-ink-50/90">
                  ({pct.toFixed(0)}%)
                </span>
              </div>
            </div>
          </div>
        );
      })}
      {funnel.lostReasons.length > 0 && (
        <div className="mt-4 rounded-md border border-ink-700 bg-ink-800 p-3">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-100">
            Lost reasons
          </div>
          <ul className="mt-2 space-y-1 text-[15px] text-ink-50">
            {funnel.lostReasons.slice(0, 6).map((r, i) => (
              <li key={i} className="flex justify-between">
                <span>{r.reason ?? "(no reason recorded)"}</span>
                <span className="font-semibold tabular-nums">{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top postcodes
// ---------------------------------------------------------------------------

function TopPostcodesSection({ days }: { days: number }): JSX.Element {
  const q = useQuery({
    queryKey: ["growth", "postcodes", days],
    queryFn: () => fetchTopPostcodes(days, 15),
  });
  return (
    <Section title="Top postcodes" subtitle={`By revenue, last ${days} days`}>
      {q.isLoading ? <Loading /> : !q.data || q.data.length === 0 ? <Empty /> : <PostcodeList rows={q.data} />}
    </Section>
  );
}

function PostcodeList({ rows }: { rows: PostcodeRow[] }): JSX.Element {
  const max = rows[0]?.revenue ?? 1;
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const pct = max > 0 ? (r.revenue / max) * 100 : 0;
        return (
          <div key={r.outward} className="grid grid-cols-12 items-center gap-3 text-[15px]">
            <div className="col-span-2 font-semibold text-ink-50">{r.outward}</div>
            <div className="col-span-7">
              <div className="h-3 rounded-sm bg-ink-800">
                <div
                  className="h-full rounded-sm"
                  style={{ width: `${pct}%`, background: STAGE_COLOR.won, opacity: 0.85 }}
                />
              </div>
            </div>
            <div className="col-span-2 text-right font-semibold tabular-nums text-ink-50">
              {formatGBP(r.revenue, { decimals: 0 })}
            </div>
            <div className="col-span-1 text-right text-[13px] tabular-nums text-ink-100">
              {r.jobCount}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lead sources — donut
// ---------------------------------------------------------------------------

function LeadSourcesSection({ days }: { days: number }): JSX.Element {
  const q = useQuery({
    queryKey: ["growth", "sources", days],
    queryFn: () => fetchLeadSources(days),
  });
  return (
    <Section title="Lead sources" subtitle={`By revenue, last ${days} days`}>
      {q.isLoading ? <Loading /> : !q.data || q.data.length === 0 ? <Empty /> : <SourceDonut rows={q.data} />}
    </Section>
  );
}

function SourceDonut({ rows }: { rows: LeadSourceRow[] }): JSX.Element {
  const data = rows.slice(0, 8).map((r) => ({
    name: r.source ?? "(unknown)",
    value: r.revenue,
    jobs: r.jobCount,
  }));
  const palette = [
    "#36C5F0", "#ECB22E", "#F07B3F", "#7F6DC5",
    "#2EB67D", "#E01E5A", "#A678A8", "#611F69",
  ];
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={50}
              outerRadius={90}
              paddingAngle={2}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={palette[i % palette.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: "#221F27",
                border: "1px solid #4A4358",
                color: "#FFFFFF",
              }}
              formatter={(value, _name, item) => {
                const v = Number(value ?? 0);
                const jobs = (item?.payload as { jobs?: number } | undefined)?.jobs ?? 0;
                return [`${formatGBP(v, { decimals: 0 })} · ${jobs} jobs`, "Revenue"];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="space-y-2 text-[15px] text-ink-50">
        {data.map((d, i) => (
          <li key={d.name} className="flex items-baseline justify-between">
            <span className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ background: palette[i % palette.length] }}
              />
              <span>{d.name}</span>
            </span>
            <span className="font-semibold tabular-nums">
              {formatGBP(d.value, { decimals: 0 })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Repeat customers
// ---------------------------------------------------------------------------

function RepeatCustomersSection({ days }: { days: number }): JSX.Element {
  const q = useQuery({
    queryKey: ["growth", "repeat", days],
    queryFn: () => fetchRepeatCustomers(days),
  });
  return (
    <Section title="Customers" subtitle={`Repeat rate + top accounts, last ${days} days`}>
      {q.isLoading ? <Loading /> : !q.data ? <Empty /> : <RepeatBlock data={q.data} />}
    </Section>
  );
}

function RepeatBlock({ data }: { data: RepeatCustomers }): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Repeat rate" value={formatPct(data.repeatRate, 0)} accent="text-stage-won" />
        <Stat label="Won jobs (window)" value={String(data.totalWonJobs)} accent="text-ink-50" />
        <Stat label="From repeats" value={String(data.jobsFromRepeatCustomers)} accent="text-ink-50" />
      </div>
      <div>
        <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-100">
          Top accounts (lifetime)
        </div>
        <ul className="mt-2 divide-y divide-ink-700">
          {data.topAccounts.map((a) => (
            <li key={a.aid} className="flex items-baseline justify-between py-2 text-[15px] text-ink-50">
              <span className="flex-1">
                <span className="font-semibold">{a.accountName ?? `Account #${a.aid}`}</span>
                <span className="ml-2 text-[13px] text-ink-100">
                  {a.lifetimeJobs} jobs · last {a.lastJobDate ?? "—"}
                </span>
              </span>
              <span className="font-semibold tabular-nums">
                {formatGBP(a.lifetimeRevenue, { decimals: 0 })}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-ink-700 bg-ink-800 p-3">
      <div className="text-[13px] uppercase tracking-[0.14em] text-ink-100">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper + helpers
// ---------------------------------------------------------------------------

export function Section({
  title,
  subtitle,
  children,
  right,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-ink-700 bg-ink-700/60 p-5 shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-ink-50">{title}</h2>
          {subtitle && <p className="mt-1 text-[13px] text-ink-100">{subtitle}</p>}
        </div>
        {right}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Loading(): JSX.Element {
  return <div className="text-[15px] text-ink-100">Loading…</div>;
}

function Empty({ message }: { message?: string } = {}): JSX.Element {
  return (
    <div className="text-[15px] text-ink-100">{message ?? "No data for this period."}</div>
  );
}

// ---------------------------------------------------------------------------
// Long-term performance — multi-year revenue + jobs + active techs + ads
// ---------------------------------------------------------------------------

function LongTermSection(): JSX.Element {
  const qc = useQueryClient();
  const [months, setMonths] = useState<number>(36);
  const [adSync, setAdSync] = useState<{ status: "idle" | "running" | "ok" | "err"; msg: string | null }>({
    status: "idle",
    msg: null,
  });
  const q = useQuery<LongTermSummary>({
    queryKey: ["growth", "long-term", months],
    queryFn: () => fetchLongTerm(months),
  });

  async function handleSyncAds(monthsBack: number): Promise<void> {
    setAdSync({ status: "running", msg: null });
    try {
      const r = await syncAds(monthsBack);
      setAdSync({
        status: "ok",
        msg: `Pulled ${r.rowsPulled} rows (${r.start} → ${r.end}) — ${r.inserted} new, ${r.updated} refreshed.`,
      });
      await qc.invalidateQueries({ queryKey: ["growth", "long-term"] });
    } catch (err) {
      setAdSync({ status: "err", msg: (err as Error).message });
    }
  }

  return (
    <Section
      title="Long-term performance"
      subtitle={`Last ${months} months — revenue, jobs, active techs, ad spend & CPC`}
      right={
        <div className="flex items-center gap-2">
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
          >
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
            <option value={36}>36 months</option>
            <option value={48}>48 months</option>
            <option value={60}>60 months</option>
          </select>
          <button
            onClick={() => void handleSyncAds(Math.max(months, 24))}
            disabled={adSync.status === "running"}
            className="rounded-md bg-aubergine px-3 py-1 text-[13px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
            title="Pull historical Google Ads data into the chart"
          >
            {adSync.status === "running" ? "Pulling…" : "Sync Ads"}
          </button>
        </div>
      }
    >
      {adSync.msg && (
        <div
          className={`mb-3 rounded-md border p-2 text-[13px] ${
            adSync.status === "err"
              ? "border-stage-lost/40 bg-stage-lost/10 text-stage-lost"
              : "border-stage-won/40 bg-stage-won/10 text-stage-won"
          }`}
        >
          {adSync.msg}
        </div>
      )}
      {q.isLoading ? (
        <Loading />
      ) : !q.data ? (
        <Empty />
      ) : (
        <LongTermBlock data={q.data} />
      )}
    </Section>
  );
}

function LongTermBlock({ data }: { data: LongTermSummary }): JSX.Element {
  const points = data.months.map((m) => ({
    month: monthLabel(m.month),
    revenue: m.revenue,
    jobs: m.jobCount,
    techs: m.activeTechCount,
    adSpend: m.adSpend,
    cpc: m.avgCpc,
  }));

  const yoyRev = data.yoy.revenuePrior > 0
    ? ((data.yoy.revenueCurrent - data.yoy.revenuePrior) / data.yoy.revenuePrior) * 100
    : null;
  const yoyJobs = data.yoy.jobsPrior > 0
    ? ((data.yoy.jobsCurrent - data.yoy.jobsPrior) / data.yoy.jobsPrior) * 100
    : null;

  return (
    <div className="space-y-5">
      {/* YoY tiles + best month tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <YoyTile label="Revenue YTD" current={formatGBP(data.yoy.revenueCurrent, { decimals: 0 })} prior={formatGBP(data.yoy.revenuePrior, { decimals: 0 })} pct={yoyRev} />
        <YoyTile label="Jobs YTD" current={String(data.yoy.jobsCurrent)} prior={String(data.yoy.jobsPrior)} pct={yoyJobs} />
        <YoyTile
          label="Peak techs (this yr / last yr)"
          current={String(data.yoy.techsCurrent)}
          prior={String(data.yoy.techsPrior)}
          pct={null}
        />
        <PeakTile data={data} />
      </div>

      {/* Multi-line chart */}
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#332D3A" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 12 }} />
            <YAxis
              yAxisId="rev"
              stroke="#2EB67D"
              tick={{ fill: "#2EB67D", fontSize: 12 }}
              tickFormatter={(v: number) => formatGBP(v, { decimals: 0 })}
            />
            <YAxis
              yAxisId="count"
              orientation="right"
              stroke="#36C5F0"
              tick={{ fill: "#36C5F0", fontSize: 12 }}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{ background: "#221F27", border: "1px solid #4A4358", color: "#FFFFFF" }}
              formatter={(value, name) => {
                const v = Number(value ?? 0);
                if (name === "revenue") return [formatGBP(v, { decimals: 0 }), "Revenue"];
                if (name === "adSpend") return [formatGBP(v, { decimals: 0 }), "Ad spend"];
                if (name === "cpc") return [formatGBP(v, { decimals: 2 }), "Avg CPC"];
                if (name === "jobs") return [String(v), "Jobs"];
                if (name === "techs") return [String(v), "Active techs"];
                return [String(value ?? ""), String(name ?? "")];
              }}
            />
            <Legend wrapperStyle={{ color: "#F0F0F0", fontSize: 12 }} />
            <Bar yAxisId="rev" dataKey="revenue" fill="#2EB67D" radius={[3, 3, 0, 0]} />
            <Bar yAxisId="rev" dataKey="adSpend" fill="#E01E5A" radius={[3, 3, 0, 0]} />
            <Line yAxisId="count" type="monotone" dataKey="jobs" stroke="#36C5F0" strokeWidth={2} dot={false} />
            <Line yAxisId="count" type="monotone" dataKey="techs" stroke="#ECB22E" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* CPC mini-chart — separate axis since values are sub-£ scale */}
      <div className="h-44 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#332D3A" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 11 }} />
            <YAxis stroke="#A678A8" tick={{ fill: "#A678A8", fontSize: 11 }} tickFormatter={(v: number) => formatGBP(v, { decimals: 2 })} />
            <Tooltip
              contentStyle={{ background: "#221F27", border: "1px solid #4A4358", color: "#FFFFFF" }}
              formatter={(value) => [formatGBP(Number(value ?? 0), { decimals: 2 }), "Avg CPC"]}
            />
            <Line type="monotone" dataKey="cpc" stroke="#A678A8" strokeWidth={2} dot={false} connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="text-[13px] text-ink-100">
        Active techs counted as distinct technicians with at least one completed visit in that
        month. With sub-contracted commission costs, profit scales with both revenue AND the
        tech count working that month — the line above shows your real capacity over time.
        Ad data populates after a "Sync Ads" pull (top-right).
      </p>
    </div>
  );
}

function YoyTile({
  label,
  current,
  prior,
  pct,
}: {
  label: string;
  current: string;
  prior: string;
  pct: number | null;
}): JSX.Element {
  const tone = pct === null ? "text-ink-100" : pct > 0 ? "text-stage-won" : pct < 0 ? "text-stage-lost" : "text-ink-100";
  const sign = pct === null ? "—" : `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-700/60 p-4">
      <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-ink-50">{current}</span>
        <span className={`text-[13px] tabular-nums ${tone}`}>{sign}</span>
      </div>
      <div className="mt-1 text-[12px] text-ink-100">vs {prior} prior year</div>
    </div>
  );
}

function PeakTile({ data }: { data: LongTermSummary }): JSX.Element {
  const best = data.bestRevenueMonth;
  if (!best) {
    return (
      <div className="rounded-lg border border-ink-700 bg-ink-700/60 p-4 text-[13px] text-ink-100">
        No data
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-700/60 p-4">
      <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">Peak month</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-ink-50">
        {formatGBP(best.revenue, { decimals: 0 })}
      </div>
      <div className="mt-1 text-[12px] text-ink-100">
        {monthLabel(best.month)} · {best.jobCount} jobs · {best.activeTechCount} techs
      </div>
      {data.peakTechMonth && data.peakTechCount !== best.activeTechCount && (
        <div className="mt-1 text-[11px] text-ink-100/80">
          Peak techs ({data.peakTechCount}): {monthLabel(data.peakTechMonth)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase B widgets (driven by the Metro Bank import)
// ---------------------------------------------------------------------------

function TopPayeesSection(): JSX.Element {
  const [days, setDays] = useState<number>(365);
  const q = useQuery<TopPayeeRow[]>({
    queryKey: ["growth", "top-payees", days],
    queryFn: () => fetchTopPayees(days, 25),
  });
  const max = (q.data ?? []).reduce((m, r) => (r.total > m ? r.total : m), 0);
  return (
    <Section
      title="Top payees"
      subtitle={`Where the money goes — last ${days === 365 ? "12 months" : days === 730 ? "24 months" : `${days} days`}`}
      right={
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
        >
          <option value={90}>90 days</option>
          <option value={365}>12 months</option>
          <option value={730}>24 months</option>
          <option value={1095}>36 months</option>
        </select>
      }
    >
      {q.isLoading ? <Loading /> : !q.data || q.data.length === 0 ? <Empty /> : (
        <ul className="space-y-1">
          {q.data.map((r) => {
            const pct = max > 0 ? (r.total / max) * 100 : 0;
            const yoy = r.ytdPrior > 0 ? ((r.ytdCurrent - r.ytdPrior) / r.ytdPrior) * 100 : null;
            const tone = yoy === null ? "text-ink-100" : yoy > 0 ? "text-stage-lost" : "text-stage-won";
            return (
              <li key={`${r.payee}-${r.firstSeen}`} className="grid grid-cols-12 items-center gap-3 text-[14px]">
                <div className="col-span-3 truncate font-semibold text-ink-50" title={r.payee}>
                  {r.payee}
                </div>
                <div className="col-span-1 text-[11px] text-ink-100">{r.category}</div>
                <div className="col-span-4">
                  <div className="h-2.5 rounded-sm bg-ink-800">
                    <div className="h-full rounded-sm bg-stage-lost/60" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="col-span-2 text-right font-semibold tabular-nums text-ink-50">
                  {formatGBP(r.total, { decimals: 0 })}
                </div>
                <div className="col-span-1 text-right text-[11px] text-ink-100 tabular-nums">{r.txnCount}×</div>
                <div className={`col-span-1 text-right text-[12px] tabular-nums ${tone}`}>
                  {yoy === null ? "—" : `${yoy > 0 ? "+" : ""}${yoy.toFixed(0)}%`}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function SpendTrendSection(): JSX.Element {
  const [months, setMonths] = useState<number>(36);
  const q = useQuery<SpendTrend>({
    queryKey: ["growth", "spend-trend", months],
    queryFn: () => fetchSpendTrend(months),
  });
  const palette = ["#36C5F0","#ECB22E","#F07B3F","#7F6DC5","#2EB67D","#E01E5A","#A678A8","#611F69","#999","#666"];
  // Build chart data — one row per month with category-keyed columns.
  const chartData = (q.data?.months ?? []).map((m) => ({
    month: monthLabel(m.month),
    ...m.byCategory,
  }));
  const cats = q.data?.categories ?? [];
  return (
    <Section
      title="Spend by category"
      subtitle={`Stacked monthly spend — last ${months} months`}
      right={
        <select
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          className="rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
        >
          <option value={12}>12 months</option>
          <option value={24}>24 months</option>
          <option value={36}>36 months</option>
        </select>
      }
    >
      {q.isLoading ? <Loading /> : !q.data || cats.length === 0 ? <Empty /> : (
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#332D3A" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 12 }} />
              <YAxis stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 12 }} tickFormatter={(v: number) => formatGBP(v, { decimals: 0 })} />
              <Tooltip
                contentStyle={{ background: "#221F27", border: "1px solid #4A4358", color: "#FFFFFF" }}
                formatter={(value, name) => [formatGBP(Number(value ?? 0), { decimals: 0 }), String(name ?? "")]}
              />
              <Legend wrapperStyle={{ color: "#F0F0F0", fontSize: 12 }} />
              {cats.map((c, i) => (
                <Bar key={c} dataKey={c} stackId="spend" fill={palette[i % palette.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Section>
  );
}

function BankReconciliationSection(): JSX.Element {
  const q = useQuery<BankReconciliation>({
    queryKey: ["growth", "bank-reconciliation", 24],
    queryFn: () => fetchBankReconciliation(24),
  });
  const flagged = (q.data?.months ?? []).filter((m) => m.flagged && (m.bankIncome > 0 || m.invoicedRevenue > 0));
  const chartData = (q.data?.months ?? []).map((m) => ({
    month: monthLabel(m.month),
    bank: m.bankIncome,
    invoiced: m.invoicedRevenue,
  }));
  return (
    <Section
      title="Bank vs invoiced revenue"
      subtitle="Bank income vs B_TVISIT.TOTALPAYMENTMADE per month — flags discrepancies > 5%"
    >
      {q.isLoading ? <Loading /> : !q.data ? <Empty /> : (
        <div className="space-y-3">
          {flagged.length > 0 && (
            <div className="rounded-md border border-stage-opportunity/40 bg-stage-opportunity/10 p-3 text-[13px]">
              <div className="font-semibold text-stage-opportunity">{flagged.length} flagged month{flagged.length === 1 ? "" : "s"}</div>
              <ul className="mt-1 space-y-0.5">
                {flagged.slice(0, 4).map((m) => (
                  <li key={m.month} className="text-ink-50">
                    {monthLabel(m.month)}: bank {formatGBP(m.bankIncome, { decimals: 0 })} vs invoiced {formatGBP(m.invoicedRevenue, { decimals: 0 })} — {m.deltaPct?.toFixed(0)}% off
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="h-60 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#332D3A" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 11 }} />
                <YAxis stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 11 }} tickFormatter={(v: number) => formatGBP(v, { decimals: 0 })} />
                <Tooltip
                  contentStyle={{ background: "#221F27", border: "1px solid #4A4358", color: "#FFFFFF" }}
                  formatter={(value, name) => [formatGBP(Number(value ?? 0), { decimals: 0 }), name === "bank" ? "Bank" : "Invoiced"]}
                />
                <Legend wrapperStyle={{ color: "#F0F0F0", fontSize: 11 }} />
                <Bar dataKey="bank" fill="#36C5F0" />
                <Bar dataKey="invoiced" fill="#2EB67D" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Section>
  );
}

function CleanerPayAuditSection(): JSX.Element {
  const q = useQuery<CleanerPayAudit>({
    queryKey: ["growth", "cleaner-pay-audit", 12],
    queryFn: () => fetchCleanerPayAudit(12),
  });
  return (
    <Section
      title="Cleaner pay audit"
      subtitle="Bank-side payments vs closed cleaner statements per (tech, month)"
    >
      {q.isLoading ? <Loading /> : !q.data || q.data.rows.length === 0 ? (
        <div className="text-[13px] text-ink-100">
          No cleaner-pay audit data yet — close some cleaner statements via the Cleaner Earnings dashboard, and any bank-side mismatches will surface here.
        </div>
      ) : (
        <ul className="divide-y divide-ink-700">
          {q.data.rows.slice(0, 30).map((r) => (
            <li key={`${r.technicianId}-${r.month}`} className="grid grid-cols-12 items-center gap-3 py-2 text-[13px]">
              <div className="col-span-3 font-semibold text-ink-50">{r.nickname ?? `tech ${r.technicianId}`}</div>
              <div className="col-span-2 text-ink-100">{monthLabel(r.month)}</div>
              <div className="col-span-2 text-right tabular-nums text-ink-50">{formatGBP(r.bankPaid, { decimals: 0 })}</div>
              <div className="col-span-2 text-right tabular-nums text-ink-50">{formatGBP(r.statementPaid, { decimals: 0 })}</div>
              <div className={`col-span-3 text-right font-semibold tabular-nums ${r.flagged ? "text-stage-lost" : "text-stage-won"}`}>
                {r.difference >= 0 ? "+" : ""}{formatGBP(r.difference, { decimals: 0 })}
                {r.flagged && <span className="ml-2 text-[11px]">⚠</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Phase C — Growth analyst (Claude commentary)
// ---------------------------------------------------------------------------

function GrowthInsightSection(): JSX.Element {
  const qc = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const q = useQuery<GrowthInsight[]>({
    queryKey: ["growth", "insights"],
    queryFn: () => fetchInsights(12),
  });
  const latest = q.data?.[0];
  const ageHours = latest
    ? Math.round((Date.now() - new Date(latest.generatedAt).getTime()) / 3_600_000)
    : null;

  async function handleRefresh(): Promise<void> {
    setRefreshing(true);
    setError(null);
    try {
      await refreshInsight();
      await qc.invalidateQueries({ queryKey: ["growth", "insights"] });
      setShowFull(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <Section
      title="Growth analyst"
      subtitle={
        latest
          ? `Last refreshed ${ageHours !== null && ageHours < 48 ? `${ageHours}h ago` : new Date(latest.generatedAt).toLocaleDateString("en-GB")} · ${latest.model}`
          : "Click 'Refresh now' to generate the first analysis"
      }
      right={
        <div className="flex items-center gap-2">
          {q.data && q.data.length > 1 && (
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:bg-ink-700"
            >
              {showHistory ? "− history" : `+ history (${q.data.length})`}
            </button>
          )}
          <button
            onClick={() => void handleRefresh()}
            disabled={refreshing}
            className="rounded-md bg-aubergine px-3 py-1 text-[13px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
          >
            {refreshing ? "Generating…" : "Refresh now"}
          </button>
        </div>
      }
    >
      {error && (
        <div className="mb-3 rounded-md border border-stage-lost/40 bg-stage-lost/10 p-2 text-[13px] text-stage-lost">
          {error}
        </div>
      )}
      {q.isLoading ? (
        <Loading />
      ) : !latest ? (
        <div className="text-[15px] text-ink-100">
          No insights yet. Click "Refresh now" to generate the first one (~10 seconds).
        </div>
      ) : (
        <div className="space-y-3">
          {latest.headline && (
            <div className="rounded-md border border-aubergine/40 bg-aubergine/10 p-3">
              <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">Headline</div>
              <div className="mt-1 text-[15px] font-semibold text-ink-50">{latest.headline}</div>
            </div>
          )}
          <button
            onClick={() => setShowFull((v) => !v)}
            className="text-[13px] text-ink-100 hover:text-ink-50"
          >
            {showFull ? "− hide full analysis" : "+ show full analysis"}
          </button>
          {showFull && (
            <div className="prose prose-invert max-w-none rounded-md border border-ink-700 bg-ink-800/40 p-4 text-[14px] text-ink-50">
              <MarkdownBody body={latest.bodyMarkdown} />
            </div>
          )}
          {showHistory && q.data && q.data.length > 1 && (
            <div className="mt-3 space-y-2 border-t border-ink-700 pt-3">
              <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">Earlier insights</div>
              {q.data.slice(1).map((i) => (
                <details key={i.id} className="rounded-md border border-ink-700 bg-ink-800/40 p-2">
                  <summary className="cursor-pointer text-[13px]">
                    <span className="font-semibold text-ink-50">{new Date(i.generatedAt).toLocaleDateString("en-GB")}</span>
                    <span className="ml-2 text-ink-100">{i.headline ?? "(no headline)"}</span>
                  </summary>
                  <div className="prose prose-invert mt-2 max-w-none text-[13px] text-ink-50">
                    <MarkdownBody body={i.bodyMarkdown} />
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

// Minimal markdown renderer — handles headings, bullets, bold, paragraphs.
// Avoids pulling in a full markdown library for the sake of one widget.
function MarkdownBody({ body }: { body: string }): JSX.Element {
  const lines = body.split("\n");
  const out: JSX.Element[] = [];
  let para: string[] = [];
  let list: string[] = [];

  function flushPara(): void {
    if (para.length === 0) return;
    out.push(<p key={`p-${out.length}`}>{renderInline(para.join(" "))}</p>);
    para = [];
  }
  function flushList(): void {
    if (list.length === 0) return;
    out.push(
      <ul key={`u-${out.length}`} className="ml-5 list-disc space-y-0.5">
        {list.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
      </ul>
    );
    list = [];
  }

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      flushList();
      const level = h[1]!.length;
      const text = h[2]!;
      const className =
        level <= 2 ? "mt-3 mb-1 text-base font-semibold text-ink-50"
        : level === 3 ? "mt-2 mb-1 text-sm font-semibold text-ink-50"
        : "mt-2 mb-1 text-sm font-semibold text-ink-100";
      out.push(<div key={`h-${out.length}`} className={className}>{renderInline(text)}</div>);
      continue;
    }
    const li = /^[-*]\s+(.*)$/.exec(line);
    if (li) {
      flushPara();
      list.push(li[1]!);
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return <>{out}</>;
}

function renderInline(text: string): JSX.Element {
  // Bold (**foo**) only — keeping the parser tiny.
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return (
    <>
      {parts.map((part, i) => {
        if (/^\*\*[^*]+\*\*$/.test(part)) {
          return <strong key={i} className="font-semibold text-ink-50">{part.slice(2, -2)}</strong>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// This week's focus — top of dashboard, 3 ticked-off actions from latest insight
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Spend cuts plan — Claude-driven cancellation/renegotiation analysis
// ---------------------------------------------------------------------------

function SpendCutsSection(): JSX.Element {
  const qc = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);
  const [showFull, setShowFull] = useState(true); // open by default — this is the urgent one
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showActions, setShowActions] = useState(true);

  const insights = useQuery<GrowthInsight[]>({
    queryKey: ["growth", "spend-cuts"],
    queryFn: () => fetchSpendCuts(12),
  });
  const actionsQ = useQuery<InsightAction[]>({
    queryKey: ["growth", "spend-cuts", "actions"],
    queryFn: () => fetchLatestSpendCutActions(),
  });

  const latest = insights.data?.[0];
  const ageHours = latest
    ? Math.round((Date.now() - new Date(latest.generatedAt).getTime()) / 3_600_000)
    : null;

  async function handleRefresh(): Promise<void> {
    setRefreshing(true);
    setError(null);
    try {
      await refreshSpendCuts();
      await qc.invalidateQueries({ queryKey: ["growth", "spend-cuts"] });
      setShowFull(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  async function toggleAction(action: InsightAction): Promise<void> {
    const newDone = !action.doneAt;
    await patchAction(action.id, { done: newDone });
    await qc.invalidateQueries({ queryKey: ["growth", "spend-cuts", "actions"] });
  }

  return (
    <Section
      title="Spend cuts plan"
      subtitle={
        latest
          ? `Last refreshed ${ageHours !== null && ageHours < 48 ? `${ageHours}h ago` : new Date(latest.generatedAt).toLocaleDateString("en-GB")} · cancellations + renegotiations`
          : "Click 'Plan' to generate the first cancellation plan from your recurring outgoings"
      }
      right={
        <div className="flex items-center gap-2">
          {insights.data && insights.data.length > 1 && (
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:bg-ink-700"
            >
              {showHistory ? "− history" : `+ history (${insights.data.length})`}
            </button>
          )}
          <button
            onClick={() => void handleRefresh()}
            disabled={refreshing}
            className="rounded-md bg-stage-lost px-3 py-1 text-[13px] font-semibold text-ink-50 hover:bg-stage-lost/80 disabled:opacity-40"
            title="Generate a fresh cancellation/renegotiation plan from your current recurring outgoings"
          >
            {refreshing ? "Generating…" : "Plan"}
          </button>
        </div>
      }
    >
      {error && (
        <div className="mb-3 rounded-md border border-stage-lost/40 bg-stage-lost/10 p-2 text-[13px] text-stage-lost">
          {error}
        </div>
      )}

      {/* Action checkboxes — at the top because they're the actionable bit */}
      {actionsQ.data && actionsQ.data.length > 0 && (
        <div className="mb-4">
          <div className="flex items-baseline justify-between">
            <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-100">
              This week's cuts ({actionsQ.data.filter((a) => a.doneAt).length}/{actionsQ.data.length} done)
            </div>
            <button
              onClick={() => setShowActions((v) => !v)}
              className="text-[12px] text-ink-100 hover:text-ink-50"
            >
              {showActions ? "hide" : "show"}
            </button>
          </div>
          {showActions && (
            <ul className="mt-2 space-y-2">
              {actionsQ.data.map((a) => (
                <li
                  key={a.id}
                  className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                    a.doneAt
                      ? "border-stage-won/40 bg-stage-won/10"
                      : "border-stage-lost/40 bg-stage-lost/10"
                  }`}
                >
                  <button
                    onClick={() => void toggleAction(a)}
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                      a.doneAt
                        ? "border-stage-won bg-stage-won text-ink-900"
                        : "border-stage-lost hover:border-ink-50"
                    }`}
                  >
                    {a.doneAt && <span className="text-[14px] leading-none">✓</span>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[15px] ${a.doneAt ? "text-ink-100 line-through" : "font-semibold text-ink-50"}`}>
                      {a.title}
                    </div>
                    {(a.metric || a.estimatedImpact) && (
                      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[12px] text-ink-100">
                        {a.metric && <span>{a.metric}</span>}
                        {a.estimatedImpact && <span className="font-semibold text-stage-won">{a.estimatedImpact}</span>}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {insights.isLoading ? (
        <Loading />
      ) : !latest ? (
        <div className="rounded-md border border-stage-lost/40 bg-stage-lost/10 p-4 text-[14px] text-ink-50">
          <div className="font-semibold">No spend-cut plan yet.</div>
          <div className="mt-1 text-ink-100">
            Click <span className="font-semibold text-stage-lost">Plan</span> to ask Claude to scan your recurring outgoings and identify cancellations + renegotiations. ~10 seconds.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {latest.headline && (
            <div className="rounded-md border border-stage-lost/40 bg-stage-lost/10 p-3">
              <div className="text-[12px] uppercase tracking-[0.14em] text-stage-lost">Biggest single win</div>
              <div className="mt-1 text-[15px] font-semibold text-ink-50">{latest.headline}</div>
            </div>
          )}
          <button
            onClick={() => setShowFull((v) => !v)}
            className="text-[13px] text-ink-100 hover:text-ink-50"
          >
            {showFull ? "− hide full plan" : "+ show full plan"}
          </button>
          {showFull && (
            <div className="prose prose-invert max-w-none rounded-md border border-ink-700 bg-ink-800/40 p-4 text-[14px] text-ink-50">
              <MarkdownBody body={latest.bodyMarkdown} />
            </div>
          )}
          {showHistory && insights.data && insights.data.length > 1 && (
            <div className="mt-3 space-y-2 border-t border-ink-700 pt-3">
              <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">Earlier plans</div>
              {insights.data.slice(1).map((i) => (
                <details key={i.id} className="rounded-md border border-ink-700 bg-ink-800/40 p-2">
                  <summary className="cursor-pointer text-[13px]">
                    <span className="font-semibold text-ink-50">{new Date(i.generatedAt).toLocaleDateString("en-GB")}</span>
                    <span className="ml-2 text-ink-100">{i.headline ?? "(no headline)"}</span>
                  </summary>
                  <div className="prose prose-invert mt-2 max-w-none text-[13px] text-ink-50">
                    <MarkdownBody body={i.bodyMarkdown} />
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function ThisWeekFocusSection(): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery<InsightAction[]>({
    queryKey: ["growth", "actions", "latest"],
    queryFn: () => fetchLatestActions(),
  });

  async function toggle(action: InsightAction): Promise<void> {
    const newDone = !action.doneAt;
    await patchAction(action.id, { done: newDone });
    await qc.invalidateQueries({ queryKey: ["growth", "actions"] });
  }

  const actions = q.data ?? [];
  const allDone = actions.length > 0 && actions.every((a) => a.doneAt);

  return (
    <Section
      title="This week's focus"
      subtitle={
        actions.length === 0
          ? "Generate a growth analysis below to populate the 3 next-actions for this week"
          : allDone
          ? "All 3 done — refresh the analyst below for the next week's focus"
          : `${actions.filter((a) => a.doneAt).length} of ${actions.length} done`
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : actions.length === 0 ? (
        <div className="text-[15px] text-ink-100">
          No actions yet. Click "Refresh now" on the Growth analyst section below.
        </div>
      ) : (
        <ul className="space-y-2">
          {actions.map((a) => (
            <li
              key={a.id}
              className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                a.doneAt
                  ? "border-stage-won/40 bg-stage-won/10"
                  : "border-ink-700 bg-ink-700/40"
              }`}
            >
              <button
                onClick={() => void toggle(a)}
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                  a.doneAt
                    ? "border-stage-won bg-stage-won text-ink-900"
                    : "border-ink-500 hover:border-ink-50"
                }`}
                title={a.doneAt ? "Mark not done" : "Mark done"}
              >
                {a.doneAt && <span className="text-[14px] leading-none">✓</span>}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-[12px] font-bold text-ink-100">{a.ordinal}.</span>
                  <span className={`text-[15px] ${a.doneAt ? "text-ink-100 line-through" : "font-semibold text-ink-50"}`}>
                    {a.title}
                  </span>
                </div>
                {(a.metric || a.estimatedImpact) && (
                  <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[12px] text-ink-100">
                    {a.metric && (
                      <span><span className="text-ink-100/60">moves:</span> <span className="font-semibold text-ink-50/80">{a.metric}</span></span>
                    )}
                    {a.estimatedImpact && (
                      <span><span className="text-ink-100/60">impact:</span> {a.estimatedImpact}</span>
                    )}
                  </div>
                )}
                {a.doneAt && a.doneBy && (
                  <div className="mt-1 text-[11px] text-stage-won/80">
                    Done by {a.doneBy} · {new Date(a.doneAt).toLocaleDateString("en-GB")}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
